/* @jsx h */
import barImageURL from "@app/images/landing/bar.jpeg";
import cashierImageURL from "@app/images/landing/catering-hero-WEB.jpeg";
import lunchTableImageURL from "@app/images/landing/lunch-table.jpeg";
import tableImageURL from "@app/images/landing/table.jpeg";
import { defineComponent, h } from "vue";

export const POSSLide = defineComponent({
  name: "POSSLide",
  setup(props) {
    return () => {
      return (
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ">
          <div class="max-w-lg mx-auto">
            <div class="inline-block px-4 py-1 bg-orange-600 bg-opacity-25 text-orange-700 rounded-full mb-4 ">
              Point of Sale
            </div>
            <h3 class="text-3xl font-hero text-accent  mb-4">
              Powerful POS System That Works Like You Do
            </h3>
            <p class=" mb-6">
              Our intuitive POS adapts to your workflow, not the other way
              around. Handle orders, splits, comps, and table management with
              lightning speed.
            </p>

            <ul class="space-y-3">
              <li class="flex items-start">
                <span class="text-green-400 mr-3 mt-1">✓</span>
                <span>Customizable interface for different service styles</span>
              </li>
              <li class="flex items-start">
                <span class="text-green-400 mr-3 mt-1">✓</span>
                <span>Works online and offline — never lose an order</span>
              </li>
              <li class="flex items-start">
                <span class="text-green-400 mr-3 mt-1">✓</span>
                <span>
                  Detailed reporting on sales, items, and staff performance
                </span>
              </li>
            </ul>
          </div>

          <div class=" p-6 rounded-lg">
            <img class=" mx-auto rounded-3xl img-holder" src={barImageURL} />
          </div>
        </div>
      );
    };
  },
});

export const QROrderingSlide = defineComponent({
  name: "QROrderingSlide",
  setup() {
    return () => {
      return (
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ">
          <div class="order-2 md:order-1 ">
            {/* QR code mockup */}
            <img class=" mx-auto rounded-3xl img-holder" src={tableImageURL} />
          </div>

          <div class="order-1 md:order-2 max-w-lg mx-auto">
            <div class="inline-block px-4 py-1 bg-green-600 bg-opacity-25 text-green-700 rounded-full mb-4">
              Reservations
            </div>
            <h3 class="text-3xl font-hero text-accent mb-4 text-prominent">
              Reservations
            </h3>
            <p class=" mb-6">
              Soon you'll be able to manage table bookings directly through
              Lemmon.
            </p>

            <ul class="space-y-3">
              <li class="flex items-start">
                <span class="text-green-400 mr-3 mt-1">✓</span>
                <span>Accept reservations online</span>
              </li>
              <li class="flex items-start">
                <span class="text-green-400 mr-3 mt-1">✓</span>
                <span> Set availability and time slots</span>
              </li>
              <li class="flex items-start">
                <span class="text-green-400 mr-3 mt-1">✓</span>
                <span>Reduce no-shows with automatic reminders</span>
              </li>
            </ul>
          </div>
        </div>
      );
    };
  },
});

export const LotyaltySlide = defineComponent({
  name: "LotyaltySlide",
  setup() {
    return () => {
      return (
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div class="max-w-lg mx-auto">
            <div class="inline-block px-4 py-1 bg-yellow-500 bg-opacity-25 text-yellow-700 rounded-full mb-4">
              Lemmon Loyalty Program
            </div>
            <h3 class="text-3xl font-hero text-accent mb-4 text-prominent">
              Turn First-Time Visitors Into Regular Guests
            </h3>
            <p class=" mb-6">
              Build lasting relationships with customers through personalized
              rewards that keep them coming back for more.
            </p>

            <ul class="space-y-3">
              <li class="flex items-start">
                <span class="text-green-400 mr-3 mt-1">✓</span>
                <span>Customizable points system and reward tiers</span>
              </li>
              <li class="flex items-start">
                <span class="text-green-400 mr-3 mt-1">✓</span>
                <span>Birthday rewards and special occasion offers</span>
              </li>
              <li class="flex items-start">
                <span class="text-green-400 mr-3 mt-1">✓</span>
                <span>Customer insights to personalize the experience</span>
              </li>
            </ul>
          </div>

          <div class=" p-6 ">
            <img class="rounded-3xl img-holder" src={lunchTableImageURL} />
          </div>
        </div>
      );
    };
  },
});

export const features = [
  {
    name: "Order Management",
    image: barImageURL,
    description:
      "Streamline your restaurant operations with effortless order management, from dine-in to delivery.",
    replaces: [
      { name: "Lightspeed" },
      { name: "L'addition" },
      { name: "PI Cash" },
    ],
    subsections: [
      {
        name: "Table Management",
        icon: "table_restaurant",
        description:
          "Optimize seating, reduce wait times, and deliver a seamless dining experience with smart table management.",
      },
      {
        name: "POS",
        icon: "point_of_sale",
        description:
          "Fast, reliable, and intuitive Point of Sale designed specifically for busy restaurant environments.",
      },
      {
        name: "Webshop",
        icon: "restaurant",
        description:
          "Expand your reach and boost sales with a beautifully integrated online ordering system.",
      },
    ],
  },
  {
    name: "Loyalty & Rewards",
    image: cashierImageURL,
    description:
      "Increase guest retention and lifetime value with personalized rewards, points, and special offers.",
    replaces: [{ name: "ObyPay" }, { name: "Points.com" }],
    subsections: [
      {
        name: "Rewards",
        icon: "trophy",
        description:
          "Delight your customers with exclusive rewards and promotions tailored to their preferences.",
      },
      {
        name: "Campaigns",
        icon: "social_leaderboard",
        description:
          "Drive repeat visits with targeted marketing campaigns that turn first-timers into regulars.",
      },
    ],
  },
  {
    name: "Payments",
    image: tableImageURL,
    description:
      "Offer seamless, secure, and flexible payment options that enhance the guest experience.",
    replaces: [{ name: "SumUp" }, { name: "Square" }],
    subsections: [
      {
        name: "Terminal Payments",
        icon: "contactless",
        description:
          "Accept card, mobile, and contactless payments quickly and securely with leading payment providers like Payrexx and Nexi",
      },
      {
        name: "Online payments",
        icon: "credit_card",
        description:
          "Enable guests to order and pay online for maximum convenience and faster table turnover.",
      },
    ],
  },
];

export const FeaturesSection = defineComponent({
  name: "FeaturesSection",
  setup() {
    return () => {
      return (
        <section class="w-full mx-auto px-6 py-16 border-b   ">
          <div class="max-w-7xl mx-auto flex flex-col space-y-24">
            {features.map((feature, i) => (
              <div
                class={
                  "flex gap-10 items-center flex-col " +
                  (i % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse")
                }
              >
                <div class="flex flex-col w-full lg:max-w-lg">
                  <div class=" mx-auto">
                    <h3 class="text-6xl font-hero text-accent mb-4 text-prominent">
                      {feature.name}
                    </h3>
                    <p class=" mb-6">{feature.description}</p>
                    <div>
                      <span class="uppercase">Replaces: </span>
                      <span class="text-accent font-bold">
                        {feature.replaces.map((el) => el.name).join(", ")}
                      </span>
                    </div>
                  </div>
                  <div class="py-8">
                    {feature.subsections.map((section) => {
                      return (
                        <div class="flex gap-2 py-6">
                          <span class="material-symbols-outlined mr-1 text-9xl text-accent">
                            {section.icon}
                          </span>
                          <div class="flex-1 flex flex-col gap-3">
                            <div class="font-hero text-3xl text-accent">
                              {section.name}
                            </div>
                            <div class="py-3">{section.description}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div class=" p-6 ">
                  <img class="rounded-3xl img-holder" src={feature.image} />
                </div>
              </div>
            ))}
          </div>
        </section>
      );
    };
  },
});
